import * as React from 'react';
import { cs } from '@shared/utils/utils-classname';
import './MassPanelList.scss';

import { IReactionDisposer, action, makeObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import { deleteVolumeApi, getVolumesByProject } from '@shared/openlayers/tools/volume/utils/api';
import { measurementStore } from '@shared/openlayers/tools/volume/utils/store/measurement-store';
import { kmlToFeatures } from '@shared/openlayers/tools/volume/utils/helpers/helpers';
import { MapWrapper } from '@openlayers/MapWrapper';
import { Events, EventsGlobal } from '@shared/Events';
import { calcReference, calcTarget } from '@shared/openlayers/tools/volume/utils/helpers/helpers';
import { MapToolsStore } from '@openlayers/MapToolsStore';
import { FileTreeService } from '@shared/services/FileTreeService';
import { DateUtils } from '@shared/utils/DateUtils';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { Message } from 'primereact/message';
import { Tree as TreeNative, TreeNode } from '@shared/ui-native/Tree';
import { Measurement, Volume } from '@typings/reality-capture/volumes/models';
import { MeasurementEditor } from '@openlayers/map-measurements/MeasurementEditComponent';
import { renderContextMenuAtPosition } from '@shared/ui-react/ContextMenu';
import { Tree as TreeDS, type TreeNodeData, collectAllFolderIds } from 'design-system';
import { TreeToolbarResponsive } from '@shared/ui-react/TreeToolbar';

@observer
export class MassPanelList extends React.Component<
    {
        treeData: ITreeData;
        mapWrapper?: MapWrapper;
        mapTools?: MapToolsStore;
        editTask?: (sheet: Mass.Sheet, folderId: number) => void;
        openTask?: (sheet: Mass.Sheet) => void;
        onLoaded?: Function;
    },
    {}
> {
    @observable isLoading = false;
    @observable error: string = null;
    @observable.ref treeData: ITreeData;
    private nativeTree: TreeNative = null;
    private sourceProj = this.props.mapWrapper?.getProjection?.();
    private disposeMeasurementReaction: IReactionDisposer | null = null;
    private disposeTreeDataReaction: IReactionDisposer | null = null;
    private fetchVolumesTimeoutId: number | null = null;

    @observable.ref volumeItems: Array<Volume & Measurement> = [];
    @observable.ref private treeNodes: TreeNodeData[] = [];
    @observable private checkedIds: (number | string)[] = [];
    @observable private selectedId: number | string | null = null;
    @observable private filterText = '';
    @observable private expandedIds: (number | string)[] = [];
    @observable private hideEmptyFolders = true;
    @observable private expandOnStart = 'all';

    get simpleMeasurementItems() {
        return this.props.mapTools?.simpleMeasurementsStore.items ?? [];
    }

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.fetchAll();
        Events.on(EventsGlobal.MAP_MEASUREMENT_SAVED, this.onVolumesChanged);
        Events.on(EventsGlobal.MAP_MEASUREMENTS_LOADED, this.onMapMeasurementsChanged);

        this.disposeMeasurementReaction = reaction(
            () => measurementStore.measurements.map(m => m.uuid).join('|'),
            () => this.scheduleFetchVolumes()
        );

        this.disposeTreeDataReaction = reaction(
            () => this.treeData,
            treeData => {
                if (treeData) this.initNativeTree();
            }
        );
    }

    componentDidUpdate(prevProps) {
        if (prevProps.treeData !== this.props.treeData) {
            this.fetchAll();
        }
    }

    componentWillUnmount() {
        Events.off(EventsGlobal.MAP_MEASUREMENT_SAVED, this.onVolumesChanged);
        Events.off(EventsGlobal.MAP_MEASUREMENTS_LOADED, this.onMapMeasurementsChanged);

        this.disposeMeasurementReaction?.();
        this.disposeMeasurementReaction = null;
        this.disposeTreeDataReaction?.();
        this.disposeTreeDataReaction = null;
        this.nativeTree?.destroy();
        this.nativeTree = null;
        if (this.fetchVolumesTimeoutId != null) {
            window.clearTimeout(this.fetchVolumesTimeoutId);
            this.fetchVolumesTimeoutId = null;
        }
    }

    private initNativeTree() {
        this.nativeTree?.destroy();
        this.nativeTree = null;

        const tree = new TreeNative({
            renderView: false,
            showRoot: true,
            showCheckboxes: true,
            propagateCheckbox: 'deep',
            checkOnHighlightSelected: true,
            childFilter: (c: any) => c.isMeasurement || c.tolerance,
            hideEmptyFolders: true,
            expandOnStart: 'all',
            storageKey: 'mass-tasks-tree',
        });

        tree.on(
            'check',
            action((e, data) => {
                this.onTreeChecked(data.node, data.changed);
                // Recalculate parent indeterminate/unchecked state after a child changes
                tree.calculateCheckboxes();
                this.checkedIds = tree.getChecked().map(n => this.nodeToId(n));
            })
        );

        tree.on('render', () => {
            action(() => {
                this.nativeTree = tree;
            })();
            this.refreshNodes();
            setTimeout(() => {
                this.onMapMeasurementsChanged();
            }, 100);
        });

        tree.initAndRender(this.treeData, true);

        // Sync persisted settings from native tree to MobX store
        const storedState = tree.getStoredState();
        if (storedState?.expandOnStart) {
            this.expandOnStart = storedState.expandOnStart;
        }
        if (storedState?.hideEmptyFolders != null) {
            this.hideEmptyFolders = storedState.hideEmptyFolders;
        }

        tree.hideEmptyFolders = this.hideEmptyFolders;

        const expandedDsIds: (number | string)[] = [];
        tree.each(c => {
            if (c.isFolder() && c.state.expanded) {
                expandedDsIds.push(this.nodeToId(c));
            }
        }, true);
        this.expandedIds = expandedDsIds;
    }

    private nodeToId = (node: TreeNode): string => (node.isFolder() ? `folder-${node.id}` : `file-${node.uuid ?? node.id}`);

    private convertNode = (node: TreeNode): TreeNodeData => {
        const data = node.ajaxData as any;
        return {
            id: this.nodeToId(node),
            name: data?.nameOriginal ?? data?.name ?? '',
            type: node.isFolder() ? 'folder' : 'file',
            icon: node.isFile() ? <></> : undefined,
            children: node.children?.filter(c => !c.state.hidden).map(this.convertNode),
            data: node,
            secondaryIcon: this.buildSecondaryIcon(node),
        };
    };

    private handleClearChecked = () => {
        if (!this.nativeTree) return;
        this.simpleMeasurementItems.forEach(item => {
            this.props.mapTools?.clearMeasurementsBySource(item.uuid);
        });
        this.volumeItems.forEach(v => {
            measurementStore.setVolumeVisible(v.uuid, false);
        });
        this.nativeTree.each(
            c => {
                c.setChecked(0, false);
            },
            true,
            true
        );
        action(() => {
            this.checkedIds = [];
        })();
    };

    @action private refreshNodes(): void {
        if (!this.nativeTree) return;
        this.treeNodes = this.nativeTree.children.filter(n => !n.state.hidden).map(this.convertNode);
    }

    private buildSecondaryIcon(node: TreeNode): React.ReactNode {
        if (!node.isFile()) return undefined;
        const data = node.ajaxData as any;
        if (!data?.isMeasurement) return undefined;

        const handleDelete = (e: React.MouseEvent) => {
            e.stopPropagation();
            this.deleteByTreeNode(node);
        };

        if (data.type === 'measurement') {
            return (
                <>
                    <i
                        className='iki iki-edit'
                        title={__('js_map_measuring_tools.edit_measurement')}
                        style={{ cursor: 'pointer', marginRight: 4 }}
                        onClick={e => {
                            e.stopPropagation();
                            const measurement = this.props.mapTools?.simpleMeasurementsStore.getByUuid(node.uuid);
                            if (measurement) {
                                MeasurementEditor.open(measurement, this.props.treeData, this.props.mapTools.simpleMeasurementsStore, null);
                            }
                        }}
                    />
                    <i className='iki iki-trash' title={__('general.delete')} style={{ cursor: 'pointer' }} onClick={handleDelete} />
                </>
            );
        }

        return <i className='iki iki-trash' title={__('general.delete')} style={{ cursor: 'pointer' }} onClick={handleDelete} />;
    }

    render() {
        if (!this.treeData) return <div className='overlay-loading no-bg' style={{ height: '100%' }}></div>;

        return (
            <div id='map-panel-mass' className={cs('flex-column tree-no-leaf-icons', { 'overlay-loading': this.isLoading })}>
                {this.error && <Message severity='error' text={this.error} style={{ width: '100%' }} />}

                {this.treeNodes.length > 0 && (
                    <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
                        <TreeToolbarResponsive
                            filterText={this.filterText}
                            onFilterChange={text => {
                                this.filterText = text;
                            }}
                            onExpandAll={() => {
                                this.expandedIds = Array.from(collectAllFolderIds(this.treeNodes));
                            }}
                            onCollapseAll={() => {
                                this.expandedIds = [];
                            }}
                            hideEmptyFolders={this.hideEmptyFolders}
                            hideEmptyFoldersAlways={false}
                            onToggleHideEmptyFolders={() => {
                                this.hideEmptyFolders = !this.hideEmptyFolders;
                                this.nativeTree?.toggleHideEmptyFolders();
                                setTimeout(() => this.refreshNodes(), 50);
                            }}
                            expandOnStart={this.expandOnStart}
                            onSetExpandOnStart={value => {
                                this.expandOnStart = value;
                                this.nativeTree?.setExpandOnStart(value as any);
                            }}
                        />
                        <TreeDS
                            nodes={this.treeNodes}
                            showRoot={this.treeNodes.length === 1}
                            checkable={true}
                            checkedIds={this.checkedIds}
                            selectedId={this.selectedId}
                            expandedIds={this.expandedIds}
                            onExpandedIdsChange={ids => {
                                this.expandedIds = ids;
                                if (this.nativeTree) {
                                    this.nativeTree.each(node => {
                                        if (node.isFolder()) {
                                            node.state.expanded = ids.includes(this.nodeToId(node));
                                        }
                                    }, true);
                                    this.nativeTree.persistState();
                                }
                            }}
                            expandOnStart='all'
                            hideEmptyFolders={this.hideEmptyFolders}
                            sortChildren={true}
                            propagateCheckbox={false}
                            filterText={this.filterText}
                            renderLabel={node => {
                                const legacyData = (node.data as TreeNode)?.ajaxData as any;
                                if (!legacyData?.isMeasurement) return node.name;
                                const isVolume = legacyData.type !== 'measurement';
                                return (
                                    <>
                                        {isVolume && <i className='iki iki-models' style={{ marginRight: 4, fontSize: '1.1em' }} />}
                                        {!isVolume && legacyData.hasArea && <i className='iki iki-area' style={{ marginRight: 4, fontSize: '1.1em' }} />}
                                        {!isVolume && legacyData.hasDistance && <i className='iki iki-measure' style={{ marginRight: 4, fontSize: '1.1em' }} />}
                                        {node.name}
                                        {legacyData.isPrivate && <i className='iki iki-hide' style={{ marginLeft: 4, fontSize: '1.1em' }} />}
                                    </>
                                );
                            }}
                            onNodeSelected={node => {
                                action(() => {
                                    this.selectedId = node.id;
                                })();
                                const legacyNode = node.data as TreeNode;
                                if (legacyNode?.ajaxData?.type === 'measurement' && legacyNode.isChecked()) {
                                    this.props.mapTools?.fitMeasurementBySource(legacyNode.uuid);
                                }
                            }}
                            onNodeChecked={node => {
                                (node.data as TreeNode)?.setChecked(1);
                            }}
                            onNodeUnchecked={node => {
                                (node.data as TreeNode)?.setChecked(0);
                            }}
                            onContextMenu={(node, position) => {
                                const legacyNode = node.data as TreeNode;
                                if (!legacyNode?.isFile()) return;
                                const items = this.onTreeContextMenu(legacyNode);
                                if (items.length) {
                                    renderContextMenuAtPosition(items, position.x, position.y, 'mass-panel-context-menu', () => {});
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }

    scheduleFetchVolumes = () => {
        if (this.fetchVolumesTimeoutId != null) return;
        this.fetchVolumesTimeoutId = window.setTimeout(() => {
            this.fetchVolumesTimeoutId = null;
            this.fetchAll();
        }, 50);
    };

    onVolumesChanged = () => {
        this.scheduleFetchVolumes();
    };

    fetchAll = () => {
        this.isLoading = true;
        const volumesPromise = getVolumesByProject(kuura.getProjectUuid());
        const measurementsPromise = this.props.mapTools?.simpleMeasurementsStore.fetchAll() ?? Promise.resolve([]);

        Promise.allSettled([volumesPromise, measurementsPromise])
            .then(([volumesResult, measurementsResult]) => {
                if (volumesResult.status === 'rejected') {
                    this.error = 'Failed to load volume measurements';
                } else {
                    const resp = (volumesResult as any).value;
                    const items: any[] = Array.isArray(resp?.items) ? resp.items : Array.isArray(resp) ? resp : [];
                    this.volumeItems = items;
                }

                this.buildTreeData([...this.volumeItems, ...(measurementsResult as any).value]);
            })
            .finally(() => {
                this.isLoading = false;
            });
    };

    onMapMeasurementsChanged = () => {
        if (!this.nativeTree) return;

        this.simpleMeasurementItems.forEach(item => {
            const matched = this.props.mapTools?.matchMeasurementsCoords(item.measurements, item.uuid);
            const node = this.nativeTree.getByUuid(item.uuid);
            if (node) {
                node.setChecked(matched ? 1 : 0, true, false);
            }
        });

        this.volumeItems.forEach(volume => {
            const visible = measurementStore.isVolumeVisible(volume.uuid);
            const node = this.nativeTree.getByUuid(volume.uuid);
            if (node) {
                node.setChecked(visible ? 1 : 0, true, false);
            }
        });

        this.nativeTree.calculateCheckboxes();
        this.checkedIds = this.nativeTree.getChecked().map(n => this.nodeToId(n));
    };

    buildTreeData = (items: any[]) => {
        const treeData = JSON.parse(JSON.stringify(this.props.treeData));

        for (const measurement of items) {
            let targetFolder = treeData;
            if ((measurement as any).folderId) {
                targetFolder = FileTreeService.findNodeById(treeData, (measurement as any).folderId, true) ?? targetFolder;
            } else if ((measurement as any).folder_uuid) {
                targetFolder = FileTreeService.findNodeByUuid(treeData, (measurement as any).folder_uuid) ?? targetFolder;
            }

            if (!targetFolder.models) {
                targetFolder.models = [];
            }

            targetFolder.models.push({
                id: measurement.uuid,
                uuid: measurement.uuid,
                name: measurement.name,
                nameOriginal: measurement.name,
                type: measurement.type,
                description: measurement.description,
                folderId: targetFolder.id,
                isFile: true,
                isMeasurement: true,
                isPrivate: !!measurement.private,
                hasArea: !!measurement.measurements?.area?.length,
                hasDistance: !!measurement.measurements?.distance?.length,
                title: [measurement.name, measurement.description, measurement.createdAt ? DateUtils.formatDateTime(measurement.createdAt) : null].filter(Boolean).join('\n'),
            } as unknown as ITreeData & { isMeasurement: boolean; nameOriginal: string });
        }

        FileTreeService.eachPostOrder(treeData, 0, node => {
            if (node.models?.length) {
                node.models = node.models.sort((a, b) => alphaNumSort(a.name, b.name));
            }
        });

        this.treeData = treeData;
    };

    isVolumeNode = (node: any): boolean => {
        return node?.isFileOrLink() && (node?.ajaxData?.type == 'stockpile' || node?.ajaxData?.type == 'comparison');
    };

    getVolumeByTreeNode = (node: any) => {
        return this.volumeItems.find(v => v.uuid === node?.uuid) ?? null;
    };

    deleteVolumeMeasurement = async (volume: any) => {
        if (!volume?.uuid) return;
        measurementStore.setVolumeVisible(volume.uuid, false);
        await deleteVolumeApi(volume.uuid);
        this.fetchAll();
    };

    deleteByTreeNode = (node: TreeNode) => {
        if (!confirm(__('general.confirm_delete', node.ajaxData.nameOriginal))) return;

        if (this.isVolumeNode(node)) {
            const volume = this.getVolumeByTreeNode(node);
            this.deleteVolumeMeasurement(volume);
        } else if (node.ajaxData.type === 'measurement') {
            this.props.mapTools.simpleMeasurementsStore.delete(node.uuid).then(() => {
                this.props.mapTools.clearMeasurementsBySource(node.uuid);
                this.fetchAll();
            });
        }
    };

    onTreeContextMenu = (node: TreeNode) => {
        if (!node.isFile()) return [];

        if (node.ajaxData?.type == 'measurement') {
            const measurement = this.props.mapTools.simpleMeasurementsStore.getByUuid(node.uuid);
            if (!measurement) return [];

            return [
                {
                    label: __('js_map_measuring_tools.edit_measurement'),
                    icon: 'iki iki-edit',
                    action: () => {
                        MeasurementEditor.open(measurement, this.props.treeData, this.props.mapTools.simpleMeasurementsStore, null);
                    },
                },
                {
                    label: __('general.delete'),
                    icon: 'iki iki-trash',
                    action: () => this.deleteByTreeNode(node),
                },
            ];
        } else {
            if (!this.isVolumeNode(node)) return [];
            const volume = this.getVolumeByTreeNode(node);
            if (!volume) return [];
            return [
                {
                    label: __('general.delete'),
                    icon: 'iki iki-trash',
                    action: () => this.deleteByTreeNode(node),
                },
            ];
        }
    };

    onTreeChecked = async (_node: any, changed: TreeNode[]) => {
        let selectUuid: string;
        let toolOpen = false;
        const mapTools = this.props.mapTools;

        const promises: Promise<void>[] = [];
        changed.forEach(c => {
            if (!c.isFile()) return;

            if (c.ajaxData?.type === 'measurement') {
                const measurement = this.props.mapTools.simpleMeasurementsStore.getByUuid(c.uuid);
                if (!measurement) return;
                if (c.isChecked()) {
                    this.props.mapTools.restoreMeasurementsFromCoords(measurement.measurements, false, false, c.uuid);
                } else {
                    this.props.mapTools.clearMeasurementsBySource(c.uuid);
                }
            }
            if (this.isVolumeNode(c)) {
                const isChecked = c?.state?.checked === 1;
                const volume: Measurement = this.getVolumeByTreeNode(c);

                if (isChecked && volume) {
                    if (!toolOpen) {
                        if (mapTools) {
                            if (mapTools.isAnyToolActive()) {
                                mapTools.deactivateAllTools();
                            }
                            if (volume.type === 'comparison') {
                                mapTools.activateModelVolumeTool();
                            } else {
                                mapTools.activateStockpileVolumeTool();
                            }
                        }
                        toolOpen = true;
                    }
                    if (!measurementStore.measurementExists(volume.uuid)) {
                        promises.push(
                            (async () => {
                                const kmlText = await this.getKmlText(volume);
                                volume.feature = kmlToFeatures(kmlText, this.sourceProj)[0];
                                volume.calcReference = calcReference(volume);
                                if (volume.type === 'comparison') {
                                    volume.calcTarget = calcTarget(volume);
                                }
                                measurementStore.addMeasurement(volume);
                            })()
                        );
                    } else {
                        measurementStore.setVolumeVisible(volume.uuid, true);
                    }
                    selectUuid = volume.uuid;
                } else if (!isChecked && volume) {
                    measurementStore.setVolumeVisible(volume.uuid, false);
                }
            }
        });
        await Promise.all(promises);

        if (selectUuid && measurementStore.measurementExists(selectUuid)) {
            measurementStore.selectMeasurement(selectUuid);
        }
    };

    getKmlText = async (volume: any): Promise<string | null> => {
        const kmlFile = volume?.kml_file;
        if (typeof kmlFile === 'string' && kmlFile.trim().length > 0) {
            return kmlFile;
        }
        const kmlUrl = volume?.kml_file_url;
        if (typeof kmlUrl === 'string' && kmlUrl.trim().length > 0) {
            const res = await fetch(kmlUrl);
            return await res.text();
        }
        return null;
    };
}
